import { Button } from '@talentmesh/ui/components/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@talentmesh/ui/components/dropdown-menu';
import { MoreVertical } from 'lucide-react';
import { useMemo, useState } from 'react';
import { generatePath, useLocation } from 'react-router';
import { useAssessmentsOverviewContext } from '../Contexts/AssessmentsOverviewContext';
import RecruitmentCloseConfirmDialog from './RecruitmentCloseConfirmDialog';
import { jobsBaseUrl } from '@/AppSettings';
import { useCompanyContext } from '@/Context/CompanyContext';
import { useNotificationContext } from '@/Context/NotificationContext';
import { useAssessmentClient } from '@/Hooks/ClientHooks';
import useCompanyId from '@/Hooks/UseCompanyId';
import { AssessmentStatuses } from '@/Models/AssessmentData';
import { RecruitmentOverview } from '@/Models/RecruitmentOverview';
import QueryParams from '@/Routing/QueryParams';
import RoutePath from '@/Routing/RoutePath';
import useTalentMeshHistory from '@/Routing/useTalentMeshHistory';
import UIStrings from '@/Utils/UIStrings';

type RecruitmentMenuButtonProps = Pick<RecruitmentOverview, 'id' | 'jobAdId' | 'name' | 'status'>;

function RecruitmentMenuButton({
    id: recruitmentId,
    jobAdId,
    name: recruitmentName,
    status: recruitmentStatus,
}: RecruitmentMenuButtonProps) {
    const history = useTalentMeshHistory();
    const companyId = useCompanyId();
    const location = useLocation();
    const { currentCompanyProfile } = useCompanyContext();
    const { removeRecruitmentEntry } = useAssessmentsOverviewContext();
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const assessmentClient = useAssessmentClient();

    const closeConfirmDialog = () => {
        setOpenConfirmDialog(false);
    };

    const closeAssessmentAsync = async () => {
        await assessmentClient.closeAssessmentAsync(recruitmentId, false, false);
        removeRecruitmentEntry(recruitmentId);
    };

    const menuItems = useMemo(() => {
        type Item = { key: string; label: string; onSelect: () => void };

        const items: Item[] = [];

        if (!jobAdId) {
            items.push({
                key: 'invite-applicants-to-talent-assessments',
                label: UIStrings.InviteApplicants,
                onSelect: () => {
                    const path = generatePath(RoutePath.AttractApplicantsInvite, {
                        companyId,
                        recruitmentId,
                    });
                    history.push(path, { from: location.pathname });
                },
            });
            items.push({
                key: 'close-talent',
                label: UIStrings.CloseTalentAssessment,
                onSelect: () => setOpenConfirmDialog(true),
            });
            return items;
        }

        items.push({
            key: 'preview',
            label: UIStrings.PreviewShareJob,
            onSelect: () => {
                const url = `${jobsBaseUrl}/${currentCompanyProfile.companySlug}/jobs/${jobAdId}`;
                window.open(url, '_blank');
            },
        });
        items.push({
            key: 'edit-job',
            label: UIStrings.EditJob,
            onSelect: () => {
                history.push(`${RoutePath.EditRecruitment}?${QueryParams.JobAdId}=${jobAdId}`);
            },
        });
        items.push({
            key: 'invite-applicants-to-recruitment',
            label: UIStrings.InviteApplicants,
            onSelect: () => {
                const path = generatePath(RoutePath.AttractApplicantsInvite, {
                    companyId,
                    recruitmentId,
                });
                history.push(path, { from: location.pathname }); // wee need to preserve the location from which user has come
            },
        });
        items.push({
            key: 'close-recruitment',
            label: UIStrings.CloseRecruitment,
            onSelect: () => setOpenConfirmDialog(true),
        });

        return items;
    }, [companyId, recruitmentId, jobAdId, history, location.pathname, currentCompanyProfile.companySlug]);

    return (
        <>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        disabled={recruitmentStatus === AssessmentStatuses.Closed}
                        data-cy="menuButton"
                        aria-label="options"
                        className="disabled:border-border min-w-12 px-0"
                    >
                        <MoreVertical className="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-48">
                    {menuItems.map((item) => (
                        <DropdownMenuItem
                            key={item.key}
                            onClick={() => {
                                item.onSelect();
                            }}
                        >
                            {item.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <RecruitmentCloseConfirmDialog
                open={openConfirmDialog}
                onClose={closeConfirmDialog}
                onCancel={closeConfirmDialog}
                onOk={async () => {
                    try {
                        closeConfirmDialog();
                        await closeAssessmentAsync();
                        showSuccessToaster(`${UIStrings.RecruitmentHasBeenClosed(recruitmentName)}.`);
                    } catch {
                        showFailToaster();
                    }
                }}
            />
        </>
    );
}

export default RecruitmentMenuButton;
